"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.checkContourContinuity = checkContourContinuity;
exports.checkDoublyLinkedList = checkDoublyLinkedList;
exports.clipDifferenceOfContours = clipDifferenceOfContours;
exports.clipIntersectionOfContours = clipIntersectionOfContours;
exports.clipOffsetContours = clipOffsetContours;
exports.clipUnionOfContours = clipUnionOfContours;
exports.createRawOffsetContour = createRawOffsetContour;
exports.createRawOffsetEntities = createRawOffsetEntities;
exports.getDoublyLinkedListIterator = getDoublyLinkedListIterator;
exports.getIntersectionPointsOfDoublyLinkedList = getIntersectionPointsOfDoublyLinkedList;
exports.getIntersectionsAndDoublyLinkedLists = getIntersectionsAndDoublyLinkedLists;
exports.markDeltaWindingNumbersOfIntersections = markDeltaWindingNumbersOfIntersections;
exports.performBooleanOperation = performBooleanOperation;
exports.rawOffsetEntity = rawOffsetEntity;
exports.standardizeContourTraversalDirection = standardizeContourTraversalDirection;
var _checkAngles = require("./checkAngles");
var _computeBbox = require("./computeBbox");
var _contourProperties = require("./contourProperties");
var _contourTopology = require("./contourTopology");
var _getPointTangent = require("./getPointTangent");
var _shortenEntities = require("./shortenEntities");
var _BoundingBoxIntersector = require("./BoundingBoxIntersector");
var _vector = require("./vector");
var _intersectEntities = require("./intersectEntities");
var _entityHitTest = require("./entityHitTest");
const LIST_LIMIT = 100000;

/** Array of intersections on an entity. */
const symIsects = Symbol('intersections');
const symDoublyLinkedParent = Symbol('doublyLinkedParent');
const symHasIntersections = Symbol('hasIntersections');
const symContourList = Symbol('contourList');
const symWindingNumber = Symbol('windingNumber');
const symExteriorWindingNumber = Symbol('exteriorWindingNumber');
/**
 * Calculates the union of several contours, and returns the resulting contours. Contour traversal directions are corrected
 * as necessary so that outer contours are wound CCW and inner contours are wound CW. The contours must not be
 * degenerate or share any edges.
 * @param contours The contours to compute the union of.
 * @returns The resulting contours.
 * @example
 * ```ts
 * // Union of two contours
 * let result = clipUnionOfContours([contour1, contour2])
 * ```
 */
function clipUnionOfContours(contours) {
  const fixedContours = contours.map(c => standardizeContourTraversalDirection(c));
  return performBooleanOperation(fixedContours, 'greater_than_zero');
}

/**
 * Calculates the intersection of several contours, and returns the resulting contours. Contour traversal directions are
 * corrected as necessary so that outer contours are wound CCW and inner contours are wound CW. The contours must not be
 * degenerate or share any edges.
 * @param contours The contours to compute the intersection of.
 * @returns The resulting contours.
 * @example
 * ```ts
 * // Intersection of two contours
 * let result = clipIntersectionOfContours([contour1, contour2])
 * ```
 */
function clipIntersectionOfContours(contours) {
  const fixedContours = contours.map(c => standardizeContourTraversalDirection(c));
  return performBooleanOperation(fixedContours, 'greater_than_one');
}

/**
 * Calculates the difference when several "clip" contours are subtraced from several "subject" contours, and returns the
 * resulting contours. Contour traversal directions are corrected as necessary so that outer contours are wound CCW and
 * inner contours are wound CW. The contours must not be degenerate or share any edges.
 * @param subjectContours The subject contours to subtract from.
 * @param clipContours The clip contours to subtract from the subject contours.
 * @returns The resulting contours.
 * @example
 * ```ts
 * // Difference of two contours
 * let result = clipDifferenceOfContours([contour1], [contour2])
 * ```
 */
function clipDifferenceOfContours(subjectContours, clipContours) {
  const fixedSubjectContours = subjectContours.map(c => standardizeContourTraversalDirection(c));
  const fixedClipContours = clipContours.map(c => standardizeContourTraversalDirection(c, true)); // Reverse the clip contours
  return performBooleanOperation([...fixedSubjectContours, ...fixedClipContours], 'greater_than_zero');
}

/**
 * Performs contour boolean operations on the given contours, and returns the result according to the given fill mode.
 * Contours which are wound counterclockwise (such as outermost contours) add 1 to the winding number. Contours which
 * are wound clockwise subtract 1 from the winding number. Finally, the resulting regions with a winding number of at
 * least `fillModeMinimumWindingNumber` are kept.
 * @param contours The contours that participate in the boolean operation. Contours which are wound counterclockwise
 * @returns The resulting contours.
 * @example
 * ```ts
 * // Union of two contours
 * let result = performBooleanOperation([contour1, contour2], 'greater_than_zero')
 * 
 * // Intersection of two contours
 * let result = performBooleanOperation([contour1, contour2], 'greater_than_one')
 * 
 * // Difference of two contours: Reverse the traversal direction of the contour to be subtracted
 * let reversedContour = { ...contour2, entities: contour2.entities.map(e => ({ ...e, reversed: !e.reversed })) }
 * let result = performBooleanOperation([contour1, reversedContour], 'greater_than_zero')
 * 
 * // Remove intersecting regions of a single contour (such as when processing a raw offset contour)
 * let result = performBooleanOperation([rawOffsetContour], 'greater_than_zero')
 * 
 * // Resolve self-intersections in a single contour
 * let result = performBooleanOperation([selfIntersectingContour], 'greater_than_zero')
 * ```
 */
function performBooleanOperation(contours, fillMode) {
  if (!contours.every(c => c.isClosed)) {
    throw new Error('All contours must be closed');
  }

  // Make sure entities are connected
  contours.forEach(c => checkContourContinuity(c));

  // Find intersections and link entities
  let {
    lists,
    nonIntersectingContours
  } = getIntersectionsAndDoublyLinkedLists(contours);
  lists.forEach(list => checkDoublyLinkedList(list));

  // Mark how the winding number changes at each intersection
  lists.forEach(markDeltaWindingNumbersOfIntersections);
  let finalContours = buildResultingContours(lists, nonIntersectingContours, fillMode);

  // Remove the symbols we added to the contours
  for (let contour of finalContours) {
    delete contour[symWindingNumber];
    delete contour[symExteriorWindingNumber];
  }
  for (let contour of contours) {
    delete contour[symHasIntersections];
    delete contour[symContourList];
  }
  return finalContours;
}

/**
 * Given an array of DoublyLinkedEntities, and an array of non-intersecting contours, returns an array of contours
 * representing the result of applying the fill mode to the contours.
 * @param lists The doubly linked lists of entities
 * @param nonIntersectingContours The non-intersecting contours
 * @param fillMode The fill mode. If 'greater_than_zero', then regions with a winding number greater than 0 are kept. If
 * 'greater_than_one', then regions with a winding number greater than 1 are kept.
 */
function buildResultingContours(lists, nonIntersectingContours, fillMode) {
  let traversedContours = [];

  // Determine winding numbers of the non-intersecting contours. To do this, we need to know which contours are inside others.
  const intersectingContours = lists.map(list => {
    let entities = unwrapDoublyLinkedList(list);
    let bbox = (0, _computeBbox.computeBbox)(entities);
    if (!(0, _vector.isBboxDefined)(bbox)) throw new Error('Bbox is not defined');
    let contour = {
      id: -1,
      bbox,
      entities: unwrapDoublyLinkedList(list),
      intersecting: true,
      insideOf: [],
      isClosed: true,
      parentId: -1
    };
    return contour;
  });

  // Determine the winding number of the region outside each contour. For intersecting contours, this is the winding number at the midpoint of the first entity.
  let windingNumbers = computeContourWindingNumbers(nonIntersectingContours.concat(intersectingContours));

  // Determine the winding number of each entity on each intersecting contour.
  for (let i = 0; i < lists.length; i++) {
    var _lastEntity$isectDelt;
    // The first entity will have whatever winding number was computed by computeContourWindingNumbers.
    let currentWindingNumber = windingNumbers[i + nonIntersectingContours.length];
    for (let entity of getDoublyLinkedListIterator(lists[i])) {
      entity.rightWindingNumber = currentWindingNumber;
      // The winding number will be updated at each intersection as we traverse the contour.
      if (entity.isectDeltaWindingNumber) {
        currentWindingNumber += entity.isectDeltaWindingNumber;
      }
    }
    // Verify that the winding number is correct at the end of the contour.
    let lastEntity = lists[i].prev;
    if (lastEntity.rightWindingNumber == null) throw new Error('Expected exteriorWindingNumber to be defined');
    let expectedWindingNumber = windingNumbers[i + nonIntersectingContours.length];
    let actualWindingNumber = lastEntity.rightWindingNumber + ((_lastEntity$isectDelt = lastEntity.isectDeltaWindingNumber) !== null && _lastEntity$isectDelt !== void 0 ? _lastEntity$isectDelt : 0);
    if (actualWindingNumber !== expectedWindingNumber) {
      throw new Error(`Expected winding number of ${expectedWindingNumber} but got ${actualWindingNumber}`);
    }
  }

  // For fill mode 'greater_than_zero', we will keep regions with a winding number greater than 0. This means that we
  // will only keep entities that separate regions with winding numbers of 0 and 1, or in other words, entities with an
  // exteriorWindingNumber of 0.

  // For fill mode 'greater_than_one', we will keep regions with a winding number greater than 1. This means that we
  // will only keep entities that separate regions with winding numbers of 1 and 2, or in other words, entities with an
  // exteriorWindingNumber of 1.

  const entityIsOnBoundary = exteriorWindingNumber => {
    if (exteriorWindingNumber == null) throw new Error('Expected exteriorWindingNumber to be defined');
    switch (fillMode) {
      case 'greater_than_zero':
        return exteriorWindingNumber === 0;
      case 'greater_than_one':
        return exteriorWindingNumber === 1;
      default:
        throw new Error('Unexpected fill mode');
    }
  };
  for (let list of lists) {
    for (let initial of getDoublyLinkedListIterator(list)) {
      // Keep going until we find an entity that has the correct winding number, and that we haven't already added to a
      // final contour.
      if (initial.added) continue;
      if (!entityIsOnBoundary(initial.rightWindingNumber)) continue;

      // Start a new contour
      let newContour = {
        entities: []
      };
      let direction = 'forward';

      /**
       * Diagram of the two types of intersections, and which segments are traversed in each case
       * 
       * (XOR is not implemented)
       * 
       * Contour isectDeltaWindingNumber = +1 (enters another contour)
       * 
       *         X
       *         |/ 
       *    0    |/   1
       *         |/
       * <-------+------- 
       *  / / / /|/ / / /
       *         |/ 
       *    1    |/   2
       *         |/      
       *         V
       * 
       * Contour isectDeltaWindingNumber = -1 (exits another contour)
       * 
       *         X
       *         |/  
       *    1    |/   2    
       *         |/
       *  / / / /|/ / / /
       *  -------+------->
       *         |/
       *    0    |/   1   
       *         |/  
       *         V
       * 
       */

      let iter3 = 0;
      let current = initial;
      while (!current.added) {
        // Add this entity to the result contour, making sure to reverse it if necessary
        current.added = true;
        let newEntity = {
          ...current.entity
        };
        delete newEntity[symIsects];
        delete newEntity[symDoublyLinkedParent];
        if (direction === 'reverse') {
          newEntity.reversed = !newEntity.reversed;
        }
        newContour.entities.push(newEntity);

        // Move to the next entity, and possibly change direction if this is an intersection point.
        if (current.isectDeltaWindingNumber) {
          // Find the correct entity to move to
          if (direction === 'forward') {
            if (!current.isectNextAfter || !current.isectNextPrior) throw new Error('Expected isectNextAfter and isectNextPrior to be defined');
            if (current.isectNextAfter.rightWindingNumber === current.rightWindingNumber) {
              // I think this will be the one we move to every time
              current = current.isectNextAfter;
            } else if (current.isectNextPrior.rightWindingNumber === current.rightWindingNumber) {
              // Probably will not ever happen
              console.warn('This probably should never happen');
              current = current.isectNextPrior;
              direction = 'reverse';
            }
          } else if (direction === 'reverse') {
            if (!current.isectPrevAfter || !current.isectPrevPrior) throw new Error('Expected isectPrevAfter and isectPrevPrior to be defined');
            if (current.isectPrevAfter.rightWindingNumber === current.rightWindingNumber) {
              // Probably will not ever happen
              console.warn('This probably should never happen');
              current = current.isectPrevAfter;
              direction = 'forward';
            } else if (current.isectPrevPrior.rightWindingNumber === current.rightWindingNumber) {
              // Probably will not ever happen
              console.warn('This probably should never happen');
              current = current.isectPrevPrior;
            }
          }
        } else if (direction === 'forward') {
          // Move forward
          current = current.next;
        } else if (direction === 'reverse') {
          // Move backward
          current = current.prev;
        } else {
          throw new Error('Unexpected direction');
        }
        iter3++;
        if (iter3 > LIST_LIMIT) throw new Error('Infinite loop detected');
      }
      if (newContour.entities.length > 0) {
        newContour;
        traversedContours.push(newContour);
      }
    }
  }

  // traversedContours now contains the intersecting contours resolved into the final non-intersecting regions we're going to keep

  // Filter the original non-intersecting contours to only those with the correct winding number
  let nonIntersectingContoursToKeep = nonIntersectingContours.filter((_contour, index) => entityIsOnBoundary(windingNumbers[index]));
  let finalContours = nonIntersectingContoursToKeep;

  // If there are intersecting contours, then traversedContours is the result of the boolean operation. Add these contours
  // to the array of non-intersecting subject contours and recompute the topology.

  // Add necessary properties to the traversedContours
  for (let i = 0; i < traversedContours.length; i++) {
    let contour = traversedContours[i];
    let bbox = (0, _computeBbox.computeBbox)(contour.entities);
    if (!(0, _vector.isBboxDefined)(bbox)) {
      throw new Error('Bbox is not defined');
    }
    finalContours.push({
      ...contour,
      bbox,
      isClosed: true,
      insideOf: [],
      parentId: -1,
      id: i
    });
  }

  // Compute topology one last time (we might be able to shortcut this, but this will do for now)
  let finalTopology = (0, _contourTopology.computePartTopology)(finalContours);
  let finalContoursWithTopology = finalContours.map((contour, i) => ({
    ...contour,
    ...finalTopology[i]
  }));
  return finalContoursWithTopology;
}

/**
 * Marks the change in winding number as each entity crosses an intersecting contour.
 * @param list The doubly linked list of entities
 */
function markDeltaWindingNumbersOfIntersections(list) {
  for (let current of getDoublyLinkedListIterator(list)) {
    if (current.isectNextPrior) {
      // Get cross product of this entity and intersecting entity
      let thisTangent = (0, _getPointTangent.getPointTangentOfEntity)(current.entity, current.entity.reversed ? 0 : 1);
      let otherTangent = (0, _getPointTangent.getPointTangentOfEntity)(current.isectNextPrior.entity, current.isectNextPrior.entity.reversed ? 0 : 1);
      if (current.entity.reversed) thisTangent[1] = (0, _vector.mul)(thisTangent[1], -1);
      if (current.isectNextPrior.entity.reversed) otherTangent[1] = (0, _vector.mul)(otherTangent[1], -1);
      let isectCross = (0, _vector.cross)(thisTangent[1], otherTangent[1]);
      if (isectCross > 0) {
        // Mark as an exit point
        current.isectDeltaWindingNumber = -1;
      } else {
        // Mark as an entry point
        current.isectDeltaWindingNumber = 1;
      }
    }
  }
}

/**
 * Finds intersections and self-intersections within an array of contours, converts each contour to a doubly-linked list, and returns the doubly-linked lists.
 * @notes Side effects: Sets [symHasIntersections] and [symContourList] on each intersecting contour
 * @param contours The contours to process
 * @returns An array of doubly-linked lists
 */
function getIntersectionsAndDoublyLinkedLists(contours) {
  let lists = [];

  // Find intersections between and within the contours
  let groupedEntities = contours.map(contour => contour.entities);
  let intersector = new _BoundingBoxIntersector.BoundingBoxIntersectorGrouped(groupedEntities, e => (0, _computeBbox.computeBbox)([e]), {
    ignoreAdjacentCollisionsWithinGroup: true,
    wrapAround: true
  });
  const allIntersections = [];
  for (let candidateIntersection of intersector.getIntersections()) {
    // Check to see if the intersection is valid
    let intersections = (0, _intersectEntities.intersectEntities)(candidateIntersection[0].item, candidateIntersection[1].item).map(({
      point,
      t1,
      t2
    }) => ({
      point,
      t1,
      t2,
      entity1: candidateIntersection[0].item,
      entity2: candidateIntersection[1].item,
      contour1: contours[candidateIntersection[0].groupIndex],
      contour2: contours[candidateIntersection[1].groupIndex]
    }));
    if (intersections.length === 0) {
      continue;
    }
    allIntersections.push(...intersections);
    for (let i of [0, 1]) {
      var _candidateIntersectio, _candidateIntersectio2;
      // Wrap intersecting contours in doubly linked lists
      let contour = contours[candidateIntersection[i].groupIndex];
      if (!contour[symHasIntersections]) {
        contour[symHasIntersections] = true;
        contour[symContourList] = wrapArrayInDoublyLinkedList(contour.entities);
        checkDoublyLinkedList(contour[symContourList]);
      }

      // Record the intersections on each entity.
      (_candidateIntersectio2 = (_candidateIntersectio = candidateIntersection[i].item)[symIsects]) !== null && _candidateIntersectio2 !== void 0 ? _candidateIntersectio2 : _candidateIntersectio[symIsects] = [];
      candidateIntersection[i].item[symIsects].push(...intersections);
    }
  }

  // Add the doubly linked lists to the array of lists in the same order as the input contours
  for (let contour of contours) {
    if (contour[symHasIntersections]) {
      lists.push(contour[symContourList]);
    }
  }

  // Split each entity at the intersection points, and create a new intersection array that includes all four entities.
  for (let isect of allIntersections) {
    // console.log(`Splitting at (${isect.point.x}, ${isect.point.y})`)

    // Split the first entity
    const entityToSplit1 = isect.entity1[symDoublyLinkedParent];
    const [firstNewEntity1, lastNewEntity1] = splitDoublyLinkedListEntity(entityToSplit1, isect.t1);

    // Reassign head of linked list if necessary
    let headIndex = lists.indexOf(entityToSplit1);
    if (headIndex >= 0) {
      lists[headIndex] = firstNewEntity1;
      isect.contour1[symContourList] = firstNewEntity1;
    }

    // Alter other intersections that were on the entity that was split, changing their t parameter and entity
    // references as necessary
    let otherIntersections1 = entityToSplit1.entity[symIsects];
    for (let otherIsect of otherIntersections1) {
      if (otherIsect === isect) continue; // Skip the one we're already handling

      // otherIsect.t1 does not necessarily correspond to isect.t1. It could be t2 instead. The entities
      // could have been passed to intersectEntities in either order.
      let t1, entity1;
      if (otherIsect.entity1 === entityToSplit1.entity) {
        t1 = 't1';
        entity1 = 'entity1';
      } else if (otherIsect.entity2 === entityToSplit1.entity) {
        t1 = 't2';
        entity1 = 'entity2';
      } else {
        throw new Error('Unexpected entity');
      }

      // Reassign otherIsect[entity1] to either the first or last new entity depending on whether it is before or after this one, and change the t parameter
      if (otherIsect[t1] < isect.t1) {
        // The other t is less than the t we split at.
        if (entityToSplit1.entity.type === 'CIRCLE') {
          otherIsect[t1] = otherIsect[t1] - isect.t1 + 1;
        } else {
          otherIsect[t1] = otherIsect[t1] / isect.t1;
        }
        if (entityToSplit1.entity.reversed) {
          otherIsect[entity1] = lastNewEntity1.entity;
          lastNewEntity1.entity[symIsects].push(otherIsect);
        } else {
          otherIsect[entity1] = firstNewEntity1.entity;
          firstNewEntity1.entity[symIsects].push(otherIsect);
        }
      } else {
        // The other t is greater than the t we split at.
        if (entityToSplit1.entity.type === 'CIRCLE') {
          otherIsect[t1] = otherIsect[t1] - isect.t1;
        } else {
          otherIsect[t1] = (otherIsect[t1] - isect.t1) / (1 - isect.t1);
        }
        if (entityToSplit1.entity.reversed) {
          otherIsect[entity1] = firstNewEntity1.entity;
          firstNewEntity1.entity[symIsects].push(otherIsect);
        } else {
          otherIsect[entity1] = lastNewEntity1.entity;
          lastNewEntity1.entity[symIsects].push(otherIsect);
        }
      }

      // console.log(`Reassigned other intersection to t1 = ${otherIsect[t1]}`)
    }

    // Split the second entity
    const entityToSplit2 = isect.entity2[symDoublyLinkedParent];
    const [firstNewEntity2, lastNewEntity2] = splitDoublyLinkedListEntity(entityToSplit2, isect.t2);

    // Reassign head of linked list if necessary
    headIndex = lists.indexOf(entityToSplit2);
    if (headIndex >= 0) {
      lists[headIndex] = firstNewEntity2;
      isect.contour2[symContourList] = firstNewEntity2;
    }

    // Alter other intersections that were on the entity that was split, changing their t parameter and entity
    // references as necessary
    let otherIntersections2 = entityToSplit2.entity[symIsects];
    for (let otherIsect of otherIntersections2) {
      if (otherIsect === isect) continue; // Skip the one we're already handling

      // otherIsect.t2 does not necessarily correspond to isect.t2. It could be t1 instead. The entities
      // could have been passed to intersectEntities in either order.
      let t2, entity2;
      if (otherIsect.entity2 === entityToSplit2.entity) {
        t2 = 't2';
        entity2 = 'entity2';
      } else if (otherIsect.entity1 === entityToSplit2.entity) {
        t2 = 't1';
        entity2 = 'entity1';
      } else {
        throw new Error('Unexpected entity');
      }

      // Reassign otherIsect[entity2] to either the first or last new entity depending on whether it is before or after this one, and change the t parameter
      if (otherIsect[t2] < isect.t2) {
        // The other t is less than the t we split at.
        if (entityToSplit2.entity.type === 'CIRCLE') {
          otherIsect[t2] = otherIsect[t2] - isect.t2 + 1;
        } else {
          otherIsect[t2] = otherIsect[t2] / isect.t2;
        }
        if (entityToSplit2.entity.reversed) {
          otherIsect[entity2] = lastNewEntity2.entity;
          lastNewEntity2.entity[symIsects].push(otherIsect);
        } else {
          otherIsect[entity2] = firstNewEntity2.entity;
          firstNewEntity2.entity[symIsects].push(otherIsect);
        }
      } else {
        // The other t is greater than the t we split at.
        if (entityToSplit2.entity.type === 'CIRCLE') {
          otherIsect[t2] = otherIsect[t2] - isect.t2;
        } else {
          otherIsect[t2] = (otherIsect[t2] - isect.t2) / (1 - isect.t2);
        }
        if (entityToSplit2.entity.reversed) {
          otherIsect[entity2] = firstNewEntity2.entity;
          firstNewEntity2.entity[symIsects].push(otherIsect);
        } else {
          otherIsect[entity2] = lastNewEntity2.entity;
          lastNewEntity2.entity[symIsects].push(otherIsect);
        }
      }

      // console.log(`Reassigned other intersection to t2 = ${otherIsect[t2]}`)
    }

    // Finally, link all four of the new entities together at their intersection point (could be less than four if
    // circles were split, but this is okay)
    firstNewEntity1.isectNextPrior = firstNewEntity2;
    firstNewEntity1.isectNextAfter = lastNewEntity2;
    lastNewEntity1.isectPrevPrior = firstNewEntity2;
    lastNewEntity1.isectPrevAfter = lastNewEntity2;
    firstNewEntity2.isectNextPrior = firstNewEntity1;
    firstNewEntity2.isectNextAfter = lastNewEntity1;
    lastNewEntity2.isectPrevPrior = firstNewEntity1;
    lastNewEntity2.isectPrevAfter = lastNewEntity1;

    // Re-assign intesections of other, non-participating entities to the new entities just created
    if (firstNewEntity1.isectPrevPrior) {
      firstNewEntity1.isectPrevPrior.isectNextAfter = firstNewEntity1;
    }
    if (firstNewEntity1.isectPrevAfter) {
      firstNewEntity1.isectPrevAfter.isectPrevAfter = firstNewEntity1;
    }
    if (lastNewEntity1.isectNextPrior) {
      lastNewEntity1.isectNextPrior.isectNextPrior = lastNewEntity1;
    }
    if (lastNewEntity1.isectNextAfter) {
      lastNewEntity1.isectNextAfter.isectPrevPrior = lastNewEntity1;
    }
    if (firstNewEntity2.isectPrevPrior) {
      firstNewEntity2.isectPrevPrior.isectNextAfter = firstNewEntity2;
    }
    if (firstNewEntity2.isectPrevAfter) {
      firstNewEntity2.isectPrevAfter.isectPrevAfter = firstNewEntity2;
    }
    if (lastNewEntity2.isectNextPrior) {
      lastNewEntity2.isectNextPrior.isectNextPrior = lastNewEntity2;
    }
    if (lastNewEntity2.isectNextAfter) {
      lastNewEntity2.isectNextAfter.isectPrevPrior = lastNewEntity2;
    }

    // If anything else is referencing entityToSplit1 or entityToSplit2, reassign it to the correct new entity
    if (entityToSplit1.isectPrevPrior) {
      entityToSplit1.isectPrevPrior.isectNextAfter = firstNewEntity1;
    }
    if (entityToSplit1.isectPrevAfter) {
      entityToSplit1.isectPrevAfter.isectPrevAfter = firstNewEntity1;
    }
    if (entityToSplit1.isectNextPrior) {
      entityToSplit1.isectNextPrior.isectNextPrior = lastNewEntity1;
    }
    if (entityToSplit1.isectNextAfter) {
      entityToSplit1.isectNextAfter.isectPrevPrior = lastNewEntity1;
    }
    if (entityToSplit2.isectPrevPrior) {
      entityToSplit2.isectPrevPrior.isectNextAfter = firstNewEntity2;
    }
    if (entityToSplit2.isectPrevAfter) {
      entityToSplit2.isectPrevAfter.isectPrevAfter = firstNewEntity2;
    }
    if (entityToSplit2.isectNextPrior) {
      entityToSplit2.isectNextPrior.isectNextPrior = lastNewEntity2;
    }
    if (entityToSplit2.isectNextAfter) {
      entityToSplit2.isectNextAfter.isectPrevPrior = lastNewEntity2;
    }

    // See if it's good
    checkDoublyLinkedList(firstNewEntity1);
    checkDoublyLinkedList(firstNewEntity2);
  }

  // Remove the other symbols we added to the entities
  for (let contour of contours) {
    for (let entity of contour.entities) {
      delete entity[symIsects];
      delete entity[symDoublyLinkedParent];
    }
  }
  return {
    lists,
    nonIntersectingContours: contours.filter(c => !c[symHasIntersections])
  };
}

/**
 * Wraps an array in a doubly-linked list. Adds a symbol to each entity pointing to the parent it is wrapped in.
 * @param entities The entities to link
 * @returns The doubly-linked list
 */
function wrapArrayInDoublyLinkedList(entities) {
  let doublyLinkedList = entities.map(entity => ({
    entity
  })); // prev and next will be set below

  for (let i = 0; i < doublyLinkedList.length; i++) {
    let ip1 = (i + 1) % doublyLinkedList.length;
    let im1 = (i - 1 + doublyLinkedList.length) % doublyLinkedList.length;
    doublyLinkedList[i].prev = doublyLinkedList[im1];
    doublyLinkedList[i].next = doublyLinkedList[ip1];
    doublyLinkedList[i].entity[symDoublyLinkedParent] = doublyLinkedList[i];
  }
  return doublyLinkedList[0];
}
function unwrapDoublyLinkedList(head) {
  let entities = [];
  for (let current of getDoublyLinkedListIterator(head)) {
    entities.push(current.entity);
  }
  return entities;
}
function* getDoublyLinkedListIterator(head) {
  let current = head;
  let limit = LIST_LIMIT;
  do {
    limit--;
    yield current;
    current = current.next;
    if (limit <= 0) throw new Error('Infinite loop detected');
  } while (current !== head);
}
function checkDoublyLinkedList(head, allowOpen = false) {
  let length = 0;
  for (let current of getDoublyLinkedListIterator(head)) {
    if (process.env.NODE_ENV === 'development') {
      // Check continuity of next/prev pointers
      if (current.next.prev !== current) throw new Error(`Doubly linked list is broken at position ${length}: next.prev !== current`);
      if (current.prev.next !== current) throw new Error(`Doubly linked list is broken at position ${length}: prev.next !== current`);

      // Check consistency of entity reference
      if (current.entity[symDoublyLinkedParent] && current.entity[symDoublyLinkedParent] !== current) throw new Error(`Doubly linked list is broken at position ${length}: entity[symDoublyLinkedParent] !== current`);

      // Check consistency of intersection pointers
      if (current.isectNextAfter) {
        if (current.isectNextAfter.isectPrevPrior !== current) throw new Error(`Doubly linked list is broken at position ${length}: isectNextAfter.isectPrevPrior !== current`);
        if (!current.next.isectPrevAfter) throw new Error(`Doubly linked list is broken at position ${length}: !next.isectPrevAfter`);
        if (current.next.isectPrevAfter !== current.isectNextAfter) throw new Error(`Doubly linked list is broken at position ${length}: next.isectPrevAfter !== isectNextAfter`);
      }
      if (current.isectNextPrior) {
        if (current.isectNextPrior.isectNextPrior !== current) throw new Error(`Doubly linked list is broken at position ${length}: isectNextPrior.isectNextPrior !== current`);
        if (!current.next.isectPrevPrior) throw new Error(`Doubly linked list is broken at position ${length}: !next.isectPrevPrior`);
        if (current.next.isectPrevPrior !== current.isectNextPrior) throw new Error(`Doubly linked list is broken at position ${length}: next.isectPrevPrior !== isectNextPrior`);
      }
      if (current.isectPrevAfter) {
        if (current.isectPrevAfter.isectPrevAfter !== current) throw new Error(`Doubly linked list is broken at position ${length}: isectPrevAfter.isectPrevAfter !== current`);
        if (!current.prev.isectNextAfter) throw new Error(`Doubly linked list is broken at position ${length}: !prev.isectNextAfter`);
        if (current.prev.isectNextAfter !== current.isectPrevAfter) throw new Error(`Doubly linked list is broken at position ${length}: prev.isectNextAfter !== isectPrevAfter`);
      }
      if (current.isectPrevPrior) {
        if (current.isectPrevPrior.isectNextAfter !== current) throw new Error(`Doubly linked list is broken at position ${length}: isectPrevPrior.isectNextAfter !== current`);
        if (!current.prev.isectNextPrior) throw new Error(`Doubly linked list is broken at position ${length}: !prev.isectNextPrior`);
        if (current.prev.isectNextPrior !== current.isectPrevPrior) throw new Error(`Doubly linked list is broken at position ${length}: prev.isectNextPrior !== isectPrevPrior`);
      }

      // Check that the endpoints of the entities match (not enforced if allowOpen is true and this is the last entity)
      if (!allowOpen || current.next !== head) {
        let thisEndPt = (0, _getPointTangent.getPointTangentOfEntity)(current.entity, current.entity.reversed ? 0 : 1);
        let nextStartPt = (0, _getPointTangent.getPointTangentOfEntity)(current.next.entity, current.next.entity.reversed ? 1 : 0);
        if ((0, _vector.norm2)((0, _vector.sub)(thisEndPt[0], nextStartPt[0])) > 1e-12) throw new Error(`Doubly linked list is broken at position ${length}: endpoint of current (${thisEndPt[0].x}, ${thisEndPt[0].y}) does not match startpoint of current.next (${nextStartPt[0].x}, ${nextStartPt[0].y})`);
      }

      // Check that at intersections the tangents are parallel
      if (current.isectNextAfter || current.isectNextPrior) {
        let thisTangent = (0, _getPointTangent.getPointTangentOfEntity)(current.entity, current.entity.reversed ? 0 : 1)[1];
        let nextTangent = (0, _getPointTangent.getPointTangentOfEntity)(current.next.entity, current.next.entity.reversed ? 1 : 0)[1];
        let angleTangent = (0, _vector.angle)(thisTangent, nextTangent);
        if (Math.abs(angleTangent) > 1e-6) throw new Error(`Doubly linked list is broken at position ${length}: angleTangent=${angleTangent}`);
      }

      // Check that at intersections the points match
      if (current.isectNextAfter && current.isectNextPrior) {
        let thisPoint = (0, _getPointTangent.getPointTangentOfEntity)(current.entity, current.entity.reversed ? 0 : 1)[0];
        let otherPoint = (0, _getPointTangent.getPointTangentOfEntity)(current.isectNextAfter.entity, current.isectNextAfter.entity.reversed ? 1 : 0)[0];
        if ((0, _vector.norm2)((0, _vector.sub)(thisPoint, otherPoint)) > 1e-12) throw new Error(`Doubly linked list is broken at position ${length}: endpoint of current (${thisPoint.x}, ${thisPoint.y}) does not match endpoint of current.isectNextAfter (${otherPoint.x}, ${otherPoint.y})`);
      }

      // Check that delta winding numbers are consistent
      if (current.isectDeltaWindingNumber) {
        var _current$isectNextPri;
        if (((_current$isectNextPri = current.isectNextPrior) === null || _current$isectNextPri === void 0 ? void 0 : _current$isectNextPri.isectDeltaWindingNumber) !== -current.isectDeltaWindingNumber) {
          var _current$isectNextPri2;
          throw new Error(`Doubly linked list is broken at position ${length}: isectDeltaWindingNumber=${current.isectDeltaWindingNumber} but isectNextPrior.isectDeltaWindingNumber=${(_current$isectNextPri2 = current.isectNextPrior) === null || _current$isectNextPri2 === void 0 ? void 0 : _current$isectNextPri2.isectDeltaWindingNumber}. They should be equal and opposite.`);
        }
      }
    }
    length++;
  }
  return {
    length
  };
}
function checkContourContinuity(contour) {
  let iStop = contour.isClosed ? contour.entities.length : contour.entities.length - 1;
  for (let i = 0; i < iStop; i++) {
    let ip1 = (i + 1) % contour.entities.length;
    let current = contour.entities[i];
    let next = contour.entities[ip1];

    // Check that the endpoints of the entities match (not enforced if allowOpen is true and this is the last entity)
    let thisEndPt = (0, _getPointTangent.getPointTangentOfEntity)(current, current.reversed ? 0 : 1);
    let nextStartPt = (0, _getPointTangent.getPointTangentOfEntity)(next, next.reversed ? 1 : 0);
    if ((0, _vector.norm2)((0, _vector.sub)(thisEndPt[0], nextStartPt[0])) > 1e-12) {
      console.error(current);
      console.error(next);
      throw new Error(`Contour is broken at position ${i}: endpoint of current (${thisEndPt[0].x}, ${thisEndPt[0].y}) does not match startpoint of next (${nextStartPt[0].x}, ${nextStartPt[0].y})`);
    }
  }
}
function getIntersectionPointsOfDoublyLinkedList(head) {
  let intersectionPoints = [];
  for (let current of getDoublyLinkedListIterator(head)) {
    if (current.isectNextAfter) {
      intersectionPoints.push((0, _getPointTangent.getPointTangentOfEntity)(current.entity, current.entity.reversed ? 0 : 1)[0]);
    }
  }
  return intersectionPoints;
}
function splitDoublyLinkedListEntity(entity, t) {
  let newEntities = (0, _shortenEntities.splitEntity)(entity.entity, t);
  newEntities.forEach(entity => entity[symIsects] = []); // Clear the intersections array

  let firstNewEntity = wrapArrayInDoublyLinkedList(newEntities);
  let lastNewEntity = firstNewEntity.prev;

  // Splice the new entities into the main list
  lastNewEntity.next = entity.next;
  lastNewEntity.next.prev = lastNewEntity;
  firstNewEntity.prev = entity.prev;
  firstNewEntity.prev.next = firstNewEntity;

  // Restore references to intersecting entities
  firstNewEntity.isectPrevPrior = entity.isectPrevPrior;
  firstNewEntity.isectPrevAfter = entity.isectPrevAfter;
  lastNewEntity.isectNextPrior = entity.isectNextPrior;
  lastNewEntity.isectNextAfter = entity.isectNextAfter;
  return [firstNewEntity, lastNewEntity];
}

/**
 * Corrects contour traversal direction so that outer contours are traversed CCW and inner contours are traversed CW.
 * Other contour types are unchanged.
 * @param contour The contour to fix
 * @param reverse If true, the traversal direction is reversed
 * @returns The fixed contour, or the original contour if no changes were necessary, or the contour was not type "inner"
 * or "outer".
 */
function standardizeContourTraversalDirection(contour, reverse = false) {
  if (contour.type !== 'inner' && contour.type !== 'outer') {
    return contour;
  }
  let fixedContour = contour;
  let area = (0, _contourProperties.getContourArea)(contour);
  if (contour.type === 'inner') {
    area = -area;
  }
  if (reverse) {
    area = -area;
  }
  if (area < 0) {
    // Reverse entities
    fixedContour = {
      ...contour,
      entities: contour.entities.slice().reverse().map(entity => ({
        ...entity,
        reversed: !entity.reversed
      }))
    };
    // Invert properties like area and direction
    if (contour.properties) {
      fixedContour.properties = {
        ...contour.properties,
        area: -contour.properties.area,
        direction: contour.properties.direction === 'cw' ? 'ccw' : 'cw'
      };
    }
    if (contour.finePoly) {
      fixedContour.finePoly = contour.finePoly.slice().reverse();
    }
    if (contour.poly) {
      fixedContour.poly = contour.poly.slice().reverse();
    }
  }
  return fixedContour;
}

/**
 * Offsets an array of contours by the given offset and resolves self-intersections.
 * @param contour The contours to offset
 * @param offset The offset amount. If offset is positive, outer contours are enlarged and inner contours are shrunk. If
 * offset is negative, outer contours are shrunk and inner contour are enlarged. In either case, if the contour is open,
 * it is thickened by the absolute value of offset and becomes a closed contour.
 * @returns The offset contour
 * @link https://mcmains.me.berkeley.edu/pubs/DAC05OffsetPolygon.pdf
 */
function clipOffsetContours(contours, offset) {
  // Fix contour traversal directions
  let fixedContours = contours.map(c => standardizeContourTraversalDirection(c));

  // Generate raw offset contours (and discard empty contours)
  let rawOffsetContours = fixedContours.map(contour => createRawOffsetContour(contour, offset)).filter(contour => contour.entities.length > 0);

  // Combine offset contours and remove self-intersections
  let offsetContours = performBooleanOperation(rawOffsetContours, 'greater_than_zero');
  return offsetContours;
}

/**
 * Offsets a contour by the given offset. The resulting contour is just the raw offset and will very likely have
 * self-intersections.
 * @param contour The contour to offset
 * @param offset The offset amount. If offset is positive, an outer contour is enlarged and an inner contour is shrunk.
 * If offset is negative, an outer contour is shrunk and an inner contour is enlarged. In either case, if the contour is
 * open, it is thickened by the absolute value of offset and becomes a closed contour.
 * @returns The offset contour
 * @link https://mcmains.me.berkeley.edu/pubs/DAC05OffsetPolygon.pdf
 */
function createRawOffsetContour(contour, offset) {
  if (offset === 0) {
    return contour;
  }

  // Correct traversal direction of contours. This means that positive offsets are always to the right of the contour.

  if (!contour.isClosed) {
    offset = Math.abs(offset);
  }
  let offsetContour;
  if (contour.isClosed) {
    const fixedContour = standardizeContourTraversalDirection(contour);
    offsetContour = {
      ...fixedContour,
      entities: createRawOffsetEntities(fixedContour.entities, offset, true)
    };
  } else {
    let forwardEntities = contour.entities;
    let reverseEntities = contour.entities.slice().reverse().map(entity => ({
      ...entity,
      reversed: !entity.reversed
    }));
    let forwardOffsetEntities = createRawOffsetEntities(forwardEntities, offset, false);
    let reverseOffsetEntities = createRawOffsetEntities(reverseEntities, offset, false);

    // Join the forward and reverse offset entities with semi-circular arcs
    /**
     *               Reverse side
     *               4          3
     *            ,--+----- <---+--.
     *           /                  \
     *  Arc "O"  |   O--------> X   |   Arc "X"
     *           \                  /   
     *            '--+---> -----+--'
     *               1          2
     *               Forward side
     */

    let firstForwardEntity = forwardOffsetEntities[0];
    let lastForwardEntity = forwardOffsetEntities[forwardOffsetEntities.length - 1];
    let firstReverseEntity = reverseOffsetEntities[0];
    let lastReverseEntity = reverseOffsetEntities[reverseOffsetEntities.length - 1];
    let point1 = (0, _getPointTangent.getPointTangentOfEntity)(firstForwardEntity, firstForwardEntity.reversed ? 1 : 0)[0];
    let point2 = (0, _getPointTangent.getPointTangentOfEntity)(lastForwardEntity, lastForwardEntity.reversed ? 0 : 1)[0];
    let point3 = (0, _getPointTangent.getPointTangentOfEntity)(firstReverseEntity, firstReverseEntity.reversed ? 1 : 0)[0];
    let point4 = (0, _getPointTangent.getPointTangentOfEntity)(lastReverseEntity, lastReverseEntity.reversed ? 0 : 1)[0];
    let pointX = (0, _vector.mul)((0, _vector.add)(point2, point3), 0.5);
    let arcXStartAngle = Math.atan2(point2.y - pointX.y, point2.x - pointX.x);
    while (arcXStartAngle < 0) arcXStartAngle += 2 * Math.PI;
    let arcXEndAngle = arcXStartAngle + Math.PI;
    let arcXRadius = (0, _vector.norm)((0, _vector.sub)(point2, pointX));
    let pointO = (0, _vector.mul)((0, _vector.add)(point4, point1), 0.5);
    let arcOStartAngle = Math.atan2(point4.y - pointO.y, point4.x - pointO.x);
    while (arcOStartAngle < 0) arcOStartAngle += 2 * Math.PI;
    let arcOEndAngle = arcOStartAngle + Math.PI;
    let arcORadius = (0, _vector.norm)((0, _vector.sub)(point4, pointO));
    let offsetEntities = [...forwardOffsetEntities, {
      type: 'ARC',
      x: pointX.x,
      y: pointX.y,
      r: arcXRadius,
      startAngle: arcXStartAngle,
      endAngle: arcXEndAngle,
      reversed: false
    }, ...reverseOffsetEntities, {
      type: 'ARC',
      x: pointO.x,
      y: pointO.y,
      r: arcORadius,
      startAngle: arcOStartAngle,
      endAngle: arcOEndAngle,
      reversed: false
    }];
    offsetContour = {
      ...contour,
      entities: offsetEntities,
      isClosed: true
    };
  }
  let bbox = (0, _computeBbox.computeBbox)(offsetContour.entities);
  if ((0, _vector.isBboxDefined)(bbox)) {
    offsetContour.bbox = bbox;
  }
  return offsetContour;
}
function createRawOffsetEntities(entities, offset, closed) {
  /** Array of entities offset from the originals. */
  const unconnectedOffsetEntities = entities.map(entity => rawOffsetEntity(entity, offset));
  const offsetEntities = [];
  for (let i = 0; i < entities.length; i++) {
    // Add these offset entities to the offset contour
    let thisOffsetEntities = unconnectedOffsetEntities[i];
    offsetEntities.push(...thisOffsetEntities);
    if (!closed && i === entities.length - 1) {
      continue;
    }

    // Join entity with the next one

    let ip1 = (i + 1) % entities.length;
    let thisEntity = entities[i];
    let nextEntity = entities[ip1];
    let thisPointTangent = (0, _getPointTangent.getPointTangentOfEntity)(thisEntity, thisEntity.reversed ? 0 : 1);
    let nextPointTangent = (0, _getPointTangent.getPointTangentOfEntity)(nextEntity, nextEntity.reversed ? 1 : 0);
    if (thisEntity.reversed) thisPointTangent[1] = (0, _vector.mul)(thisPointTangent[1], -1);
    if (nextEntity.reversed) nextPointTangent[1] = (0, _vector.mul)(nextPointTangent[1], -1);
    let thisPoint = thisPointTangent[0];
    let nextPoint = nextPointTangent[0];
    let thisTangent = thisPointTangent[1];
    let nextTangent = nextPointTangent[1];

    // TODO: Check that thisPointTangent[0] and nextPointTangent[0] are the same

    if ((0, _vector.norm)((0, _vector.sub)(thisPoint, nextPoint)) > 1e-6) throw new Error(`Endpoints of entities ${i} and ${ip1} do not match`);

    // Find whether this and next are concave, convex, or parallel, and join them accordingly
    let angleToNext = (0, _vector.signedAngle)(thisTangent, nextTangent);
    let angleSign = Math.abs(angleToNext) < 1e-9 ? 0 : Math.sign(angleToNext);
    let joinType = 'direct';
    if (angleSign < 0 && offset > 0 || angleSign > 0 && offset < 0) {
      joinType = 'segments';
    } else if (angleSign > 0 && offset > 0 || angleSign < 0 && offset < 0) {
      joinType = 'arc';
    }
    if (joinType !== 'direct') {
      let nextOffsetEntities = unconnectedOffsetEntities[ip1];
      let thisOffsetEndPoint = (0, _getPointTangent.getPointTangentOfEntity)(thisOffsetEntities[thisOffsetEntities.length - 1], thisOffsetEntities[thisOffsetEntities.length - 1].reversed ? 0 : 1)[0];
      let nextOffsetStartPoint = (0, _getPointTangent.getPointTangentOfEntity)(nextOffsetEntities[0], nextOffsetEntities[0].reversed ? 1 : 0)[0];
      // Join this and the next offset entities
      switch (joinType) {
        case 'arc':
          let startAngle = Math.atan2(thisOffsetEndPoint.y - thisPoint.y, thisOffsetEndPoint.x - thisPoint.x);
          let endAngle = Math.atan2(nextOffsetStartPoint.y - nextPoint.y, nextOffsetStartPoint.x - nextPoint.x);
          let reversed = angleSign < 0;
          let arc = {
            type: 'ARC',
            x: thisPoint.x,
            y: thisPoint.y,
            r: Math.abs(offset),
            startAngle: reversed ? endAngle : startAngle,
            endAngle: reversed ? startAngle : endAngle,
            reversed
          };
          (0, _checkAngles.correctAngles)([arc]);
          offsetEntities.push(arc);
          break;
        case 'segments':
          offsetEntities.push({
            type: 'LINE',
            start: thisOffsetEndPoint,
            end: thisPoint,
            reversed: false
          }, {
            type: 'LINE',
            start: thisPoint,
            end: nextOffsetStartPoint,
            reversed: false
          });
          break;
      }
    }
  }
  return offsetEntities;
}

/**
 * Offsets an entity by the given offset, and returns an array of 0, 1, or 2 entities that are the result of the offset.
 * @note Arcs and circles that are offset by an amount that would result in a negative radius are not inverted as might
 * be done traditionally. Instead, circles are removed entirely (returning []) and arcs are converted into two connected
 * line segments, beginning at one endpoint of the inverted arc, passing through the center of the arc, and ending at
 * the other endpoint of the inverted arc (see Figure 10.c and the accompanying explanation for why this is important)
 * @param entity The entity to offset
 * @param offset The offset amount. If offset is positive, the entity is offset to the right of its tangent. If offset is
 * negative, the entity is offset to the left of its tangent.
 * @returns An array of 0, 1, or 2 entities that are the result of the offset
 */
function rawOffsetEntity(entity, offset) {
  let pointTangent = (0, _getPointTangent.getPointTangentOfEntity)(entity, entity.reversed ? 0 : 1);
  if (entity.reversed) pointTangent[1] = (0, _vector.mul)(pointTangent[1], -1);
  let tangent = pointTangent[1];

  // Offset this entity
  let offsetEntities;
  switch (entity.type) {
    case 'LINE':
      let offsetVector = (0, _vector.mul)((0, _vector.normalize)(tangent), offset);
      offsetVector = {
        x: offsetVector.y,
        y: -offsetVector.x
      }; // Rotate tangent 90 deg CW so that offset is to the right
      offsetEntities = [{
        ...entity,
        start: (0, _vector.add)(entity.start, offsetVector),
        end: (0, _vector.add)(entity.end, offsetVector)
      }];
      break;
    case 'CIRCLE':
      {
        let newRadius = entity.r + offset * (entity.reversed ? -1 : 1);
        if (newRadius > 0) {
          offsetEntities = [{
            ...entity,
            r: newRadius
          }];
        } else {
          offsetEntities = [];
        }
        break;
      }
    case 'ARC':
      {
        let newRadius = entity.r + offset * (entity.reversed ? -1 : 1);
        if (newRadius > 1e-9) {
          offsetEntities = [{
            ...entity,
            r: newRadius
          }];
        } else if (newRadius < -1e-9) {
          let invertedArc = {
            ...entity,
            r: newRadius
          };
          let startPoint = (0, _getPointTangent.getPointTangentOfEntity)(invertedArc, entity.reversed ? 1 : 0)[0];
          let endPoint = (0, _getPointTangent.getPointTangentOfEntity)(invertedArc, entity.reversed ? 0 : 1)[0];
          let center = {
            x: entity.x,
            y: entity.y
          };
          offsetEntities = [{
            type: 'LINE',
            start: startPoint,
            end: center,
            reversed: false
          }, {
            type: 'LINE',
            start: center,
            end: endPoint,
            reversed: false
          }];
        } else {
          offsetEntities = [];
        }
        break;
      }
    default:
      throw new Error('Unexpected entity type');
  }
  return offsetEntities;
}

/**
 * Computes the external winding numbers of each of the given contours.
 * @note The winding number is computed by casting a ray from the midpoint of the first entity of the contour. If the
 * contour intersects itself or other contours, this winding number will change as the contour is traversed.
 * @param contours An array of closed contours.
 * @returns An array of winding numbers, one for each contour.
 */
function computeContourWindingNumbers(contours) {
  // Create successively expanding types as we go. Begin with just the id.
  const windingNumbers = [];

  // Build neighbor list
  // For each contour, the list of other contours whose bboxs intersect
  let neighbors = [];
  for (let i = 0; i < contours.length; i++) {
    if (!(0, _vector.isBboxDefined)(contours[i].bbox)) throw new Error(`Contour ${contours[i].id}'s bounding box is not fully defined`);
    neighbors[i] = [];
  }
  let intersector = new _BoundingBoxIntersector.BoundingBoxIntersector(contours, c => c.bbox);
  for (let intersection of intersector.getIntersections()) {
    neighbors[intersection[0].index].push(intersection[1].index);
    neighbors[intersection[1].index].push(intersection[0].index);
  }

  // Determine whether each closed contour is an inner or outer contour
  // We'd also like to know which parent an open contour is inside of, too, if possible
  for (let i = 0; i < contours.length; i++) {
    const c = contours[i];

    // Get the midpoint of the first entity. For contours which have been split at each intersection, this point is
    // guaranteed not to be at an intersection.
    let [p, tan] = (0, _getPointTangent.getPointTangentOfEntity)(c.entities[0], 0.5);
    if (c.entities[0].reversed) tan = (0, _vector.mul)(tan, -1);
    // Move p slightly to the right side of the entity
    tan = (0, _vector.normalize)({
      x: tan.y,
      y: -tan.x
    }); // Rotate 90 deg CW
    p = (0, _vector.add)(p, (0, _vector.mul)(tan, 1e-9));
    let testBbox = {
      ...c.bbox
    };
    let testContours = [];
    for (let neighj = 0; neighj < neighbors[i].length; neighj++) {
      let j = neighbors[i][neighj];
      let d = contours[j];
      testBbox.min = (0, _vector.min)(testBbox.min, d.bbox.min);
      testBbox.max = (0, _vector.max)(testBbox.max, d.bbox.max);
      testContours.push(d);
    }
    let part = {
      arcified: {
        bbox: testBbox,
        contours: [...testContours, c]
      }
    };
    windingNumbers.push((0, _entityHitTest.getWindingNumberOfPoint)(p, part));
  }
  return windingNumbers;
}